import * as React from 'react'

import { Modal, Space, SubText, TransparentIconButton } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { type GlobalSearchRecentItem } from '../../../../value-objects/GlobalSearchRecentItem'
import { GlobalSearchInput } from '../GlobalSearchInput'
import { GlobalSearchRecentsList } from '../GlobalSearchRecentsList'

/**
 * Dismiss open popovers when the modal opens. Triggers ClickAwayListener
 * on Tooltip-based popovers. Fires during onEnter, after MUI captures the
 * previously focused element for restoration.
 */
const dismissPopovers = () => document.body.click()

interface IGlobalSearchModalProps {
  isOpen: boolean
  query: string
  showEnterHint: boolean
  recentItems: GlobalSearchRecentItem[]
  inputRef: React.RefObject<HTMLInputElement>
  onClose: () => void
  onQueryChange: (value: string) => void
  onSubmit: () => void
  onRecentItemClick: (item: GlobalSearchRecentItem) => void
}

export const GlobalSearchModal = ({
  isOpen,
  query,
  showEnterHint,
  recentItems,
  inputRef,
  onClose,
  onQueryChange,
  onSubmit,
  onRecentItemClick,
}: IGlobalSearchModalProps) => {
  const intl = useProcurifyIntl()

  const focusInput = () => {
    inputRef.current?.focus()
  }

  return (
    <Modal
      aria-label={intl.formatMessage(messages.modalAriaLabel)}
      maxWidth='sm'
      fullWidth
      open={isOpen}
      onClose={onClose}
      keepMounted
      TransitionProps={{
        onEnter: dismissPopovers,
        onEntered: focusInput,
      }}
    >
      <Modal.Card>
        <Modal.Content pt={0} pr={1} pb={0}>
          <Space direction='horizontal' size='small' align='center'>
            <Space.Item grow={1} flex>
              <GlobalSearchInput
                query={query}
                inputRef={inputRef}
                onQueryChange={onQueryChange}
                onSubmit={onSubmit}
              />
            </Space.Item>
            {showEnterHint && (
              <Space.Item flex>
                <SubText inline noWrap color='textTertiary'>
                  {intl.formatMessage(messages.enterToSearchHint)}
                </SubText>
              </Space.Item>
            )}
            <TransparentIconButton
              aria-label={intl.formatMessage(messages.closeButtonAriaLabel)}
              icon='times'
              onClick={onClose}
            />
          </Space>
        </Modal.Content>
        {recentItems.length > 0 && (
          <Modal.Content pl={0.5} pr={0.5} pb={1}>
            <GlobalSearchRecentsList
              items={recentItems}
              onRecentItemClick={onRecentItemClick}
            />
          </Modal.Content>
        )}
      </Modal.Card>
    </Modal>
  )
}
